import { useEffect, useState, type FormEvent } from 'react';
import type { AccountUser, NotificationsResponse, SavedView, ViewpointSubmission } from '@whaletail/shared';
import { MAX_SAVED_VIEWS, googleMapsPinUrl } from '@whaletail/shared';
import {
  ApiError,
  changePassword,
  clearHome,
  createView,
  deleteAccount,
  deleteView,
  listViews,
  myViewpoints,
  removeViewpoint,
  renameView,
  setDefaultView,
  setHome,
  submitViewpoint,
  trailLabel,
  type ViewSnapshot,
} from '../api.ts';
import type { MyLocation } from '../geolocation.ts';
import { HouseIcon } from '../icons.tsx';
import { LocationPicker, type PickedLocation } from './LocationPicker.tsx';
import { NearbyAlerts } from './NearbyAlerts.tsx';

interface Props {
  user: AccountUser;
  onUserChange: (u: AccountUser) => void;
  onSignedOut: () => void;
  /** Snapshot of the current camera and filters, or null if the map is not ready. */
  captureView: () => ViewSnapshot | null;
  onApplyView: (v: SavedView) => void;
  draft: PickedLocation | null;
  onDraftChange: (d: PickedLocation | null) => void;
  picking: boolean;
  onPickingChange: (active: boolean) => void;
  myLocation: MyLocation | null;
  onWantMyLocation: () => void;
  onFlyTo: (lat: number, lon: number, zoom?: number) => void;
  /** Refresh the map's view points after one is submitted or withdrawn. */
  onViewpointsChanged: () => void;
  /** Mirrors the current saved-views list up to App, which shows each one as a quick-access button on the map itself. */
  onViewsChanged: (views: SavedView[]) => void;
  notify: (text: string) => void;
  /** Opens the full-page admin view (AdminPage.tsx) — only rendered below when user.role is 'admin'. */
  onOpenAdminPage: () => void;
  /** Badge on the admin-page button — view points awaiting review. */
  pendingReviewCount: number;
  /** Nearby-whale alert settings as saved, for the radius ring on the map. */
  onAlertsChange?: (r: NotificationsResponse) => void;
}

type Form = 'home' | 'viewpoint' | null;

const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function PlacesPanel(props: Props) {
  const { user, onUserChange, onSignedOut, captureView, onApplyView, draft, onDraftChange, picking, onPickingChange } = props;
  const { myLocation, onWantMyLocation, onFlyTo, onViewpointsChanged, onViewsChanged, notify } = props;
  const { onOpenAdminPage, pendingReviewCount } = props;

  const [form, setForm] = useState<Form>(null);

  const [views, setViews] = useState<SavedView[]>([]);
  /** Keeps App's map-button copy of the list in sync with every local change. */
  const updateViews = (next: SavedView[]) => {
    setViews(next);
    onViewsChanged(next);
  };
  const [newViewName, setNewViewName] = useState('');
  const [viewError, setViewError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [renameText, setRenameText] = useState('');

  const [mine, setMine] = useState<ViewpointSubmission[]>([]);
  const [spotName, setSpotName] = useState('');
  const [spotRegion, setSpotRegion] = useState('');
  const [spotNote, setSpotNote] = useState('');
  const [spotHike, setSpotHike] = useState(false);
  const [spotError, setSpotError] = useState<string | null>(null);
  const [spotBusy, setSpotBusy] = useState(false);

  const [homeError, setHomeError] = useState<string | null>(null);
  const [homeBusy, setHomeBusy] = useState(false);

  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNext, setPwNext] = useState('');
  const [pwMessage, setPwMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [delPassword, setDelPassword] = useState('');
  const [delError, setDelError] = useState<string | null>(null);

  useEffect(() => {
    listViews().then(updateViews).catch(() => undefined);
    myViewpoints().then(setMine).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the panel must not leave the map stuck waiting for a click.
  useEffect(
    () => () => {
      onPickingChange(false);
      onDraftChange(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const openForm = (next: Form) => {
    onPickingChange(false);
    onDraftChange(next === 'home' && user.home ? { lat: user.home.lat, lon: user.home.lon, label: user.home.label } : null);
    setForm(next);
    setHomeError(null);
    setSpotError(null);
  };
  const closeForm = () => openForm(null);

  // --- home -----------------------------------------------------------------
  const saveHome = async () => {
    if (!draft) return setHomeError('Choose a location first.');
    setHomeBusy(true);
    setHomeError(null);
    try {
      const res = await setHome({ lat: draft.lat, lon: draft.lon, label: draft.label ?? null });
      onUserChange(res.user);
      closeForm();
      notify('Home saved.');
    } catch (e) {
      setHomeError(errText(e, 'Could not save your home location.'));
    } finally {
      setHomeBusy(false);
    }
  };

  const removeHome = async () => {
    try {
      onUserChange((await clearHome()).user);
      notify('Home removed.');
    } catch (e) {
      setHomeError(errText(e, 'Could not remove your home location.'));
    }
  };

  // --- saved views ------------------------------------------------------------
  const saveView = async (e: FormEvent) => {
    e.preventDefault();
    setViewError(null);
    const snapshot = captureView();
    if (!snapshot) return setViewError('The map is still loading.');
    const name = newViewName.trim();
    if (!name) return setViewError('Give this view a name.');
    try {
      const created = await createView({ ...snapshot, name });
      updateViews([...views, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewViewName('');
      notify(`Saved “${created.name}”.`);
    } catch (err) {
      setViewError(errText(err, 'Could not save that view.'));
    }
  };

  const commitRename = async (id: number) => {
    setViewError(null);
    try {
      updateViews(await renameView(id, renameText));
      setRenaming(null);
    } catch (err) {
      setViewError(errText(err, 'Could not rename that view.'));
    }
  };

  const toggleDefault = async (v: SavedView) => {
    setViewError(null);
    try {
      updateViews(await setDefaultView(v.isDefault ? null : v.id));
    } catch (err) {
      setViewError(errText(err, 'Could not change the default view.'));
    }
  };

  const dropView = async (id: number) => {
    try {
      updateViews(await deleteView(id));
    } catch (err) {
      setViewError(errText(err, 'Could not delete that view.'));
    }
  };

  // --- view point suggestions -------------------------------------------------
  const submitSpot = async (e: FormEvent) => {
    e.preventDefault();
    setSpotError(null);
    if (!draft) return setSpotError('Choose the location first: type an address, enter coordinates, or click the map.');
    if (spotName.trim().length < 2) return setSpotError('Give the spot a name.');
    setSpotBusy(true);
    try {
      const res = await submitViewpoint({
        name: spotName.trim(),
        lat: draft.lat,
        lon: draft.lon,
        region: spotRegion.trim() || null,
        note: spotNote.trim() || null,
        hike: spotHike,
      });
      setMine((prev) => [res.viewpoint, ...prev]);
      setSpotName('');
      setSpotRegion('');
      setSpotNote('');
      setSpotHike(false);
      closeForm();
      onViewpointsChanged();
      notify(res.autoApproved ? 'View point added.' : 'Thanks! An admin will review your suggestion.');
    } catch (err) {
      setSpotError(errText(err, 'Could not submit that spot.'));
    } finally {
      setSpotBusy(false);
    }
  };

  const withdraw = async (id: number) => {
    try {
      await removeViewpoint(id);
      setMine((prev) => prev.filter((s) => s.id !== id));
      onViewpointsChanged();
    } catch (err) {
      setSpotError(errText(err, 'Could not remove that spot.'));
    }
  };

  // --- account ----------------------------------------------------------------
  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwMessage(null);
    try {
      await changePassword(pwCurrent, pwNext);
      setPwCurrent('');
      setPwNext('');
      setPwMessage({ ok: true, text: 'Password changed. Other devices have been signed out.' });
    } catch (err) {
      setPwMessage({ ok: false, text: errText(err, 'Could not change your password.') });
    }
  };

  const removeAccount = async (e: FormEvent) => {
    e.preventDefault();
    setDelError(null);
    try {
      await deleteAccount(delPassword);
      setDelPassword('');
      onSignedOut();
    } catch (err) {
      setDelError(errText(err, 'Could not delete your account.'));
    }
  };

  return (
    <div className="panel-scroll places">
      {/* -------------------------------------------------------------- account */}
      <div className="account places-account">
        <span className="who" title={user.role === 'admin' ? 'Administrator' : undefined}>
          {user.username}
          {user.role === 'admin' && <span className="badge badge-admin">admin</span>}
        </span>
        <button type="button" className="btn btn-quiet" onClick={onSignedOut}>
          Sign Out
        </button>
      </div>

      {user.role === 'admin' && (
        <section className="section">
          <button type="button" className="btn btn-block" onClick={onOpenAdminPage}>
            Open Admin Page
            {pendingReviewCount > 0 && (
              <span className="count-pill count-alert" aria-label={`${pendingReviewCount} awaiting review`}>
                {pendingReviewCount}
              </span>
            )}
          </button>
        </section>
      )}

      {/* ---------------------------------------------------------------- home */}
      <section className="section">
        <h3>
          <HouseIcon size={16} className="h3-icon" /> Home
        </h3>
        {form === 'home' ? (
          <>
            <LocationPicker
              value={draft}
              onChange={onDraftChange}
              picking={picking}
              onPickingChange={onPickingChange}
              myLocation={myLocation}
              onWantMyLocation={onWantMyLocation}
              onFlyTo={onFlyTo}
              requireRegion={false}
            />
            {homeError && <p className="form-error">{homeError}</p>}
            <div className="row gap">
              <button type="button" className="btn btn-primary" onClick={saveHome} disabled={homeBusy || !draft}>
                Save Home
              </button>
              <button type="button" className="btn" onClick={closeForm}>
                Cancel
              </button>
            </div>
          </>
        ) : user.home ? (
          <>
            <p className="muted">
              {user.home.label ?? `${user.home.lat.toFixed(4)}, ${user.home.lon.toFixed(4)}`}
            </p>
            <div className="row gap">
              <button type="button" className="btn" onClick={() => onFlyTo(user.home!.lat, user.home!.lon, 12)}>
                Show
              </button>
              <button type="button" className="btn" onClick={() => openForm('home')}>
                Change
              </button>
              <button type="button" className="btn btn-quiet" onClick={removeHome}>
                Remove
              </button>
            </div>
            {homeError && <p className="form-error">{homeError}</p>}
          </>
        ) : (
          <>
            <p className="muted">Set a home location and it appears on the map as a house.</p>
            <button type="button" className="btn" onClick={() => openForm('home')}>
              Set Home Location
            </button>
          </>
        )}
      </section>

      {/* ------------------------------------------------ nearby-whale alerts */}
      <NearbyAlerts user={user} notify={notify} {...(props.onAlertsChange ? { onChange: props.onAlertsChange } : {})} />

      {/* --------------------------------------------------------- saved views */}
      <section className="section">
        <h3>
          Saved Views{' '}
          <span className="count-pill">
            {views.length} / {MAX_SAVED_VIEWS}
          </span>
        </h3>
        <form onSubmit={saveView} className="row">
          <input
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            placeholder={views.length >= MAX_SAVED_VIEWS ? 'Delete a view to save another' : 'Name the current view'}
            aria-label="Name for the current view"
            maxLength={60}
            disabled={views.length >= MAX_SAVED_VIEWS}
          />
          <button type="submit" className="btn" disabled={views.length >= MAX_SAVED_VIEWS}>
            Save
          </button>
        </form>
        <small className="hint">
          Remembers the map position, zoom and view points. Each one is a button on the map, up to {MAX_SAVED_VIEWS}. Star one to
          open it every time the page loads.
        </small>
        {viewError && <p className="form-error">{viewError}</p>}

        {views.length === 0 ? (
          <p className="muted">No saved views yet.</p>
        ) : (
          <ul className="item-list">
            {views.map((v) => (
              <li key={v.id} className="item">
                {renaming === v.id ? (
                  <form
                    className="row"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void commitRename(v.id);
                    }}
                  >
                    <input value={renameText} onChange={(e) => setRenameText(e.target.value)} maxLength={60} autoFocus aria-label="New name" />
                    <button type="submit" className="btn">
                      Save
                    </button>
                    <button type="button" className="btn btn-quiet" onClick={() => setRenaming(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <button type="button" className="item-main" onClick={() => onApplyView(v)}>
                      <span className="item-title">
                        {v.isDefault && (
                          <span className="default-star" title="Opens when the page loads" aria-label="Default view">
                            ★{' '}
                          </span>
                        )}
                        {v.name}
                      </span>
                      <span className="item-meta">
                        zoom {v.zoom.toFixed(1)}
                        {v.showViewing ? ' · view points' : ''}
                      </span>
                    </button>
                    <div className="item-actions">
                      <button
                        type="button"
                        className="link-btn"
                        aria-pressed={v.isDefault}
                        onClick={() => void toggleDefault(v)}
                      >
                        {v.isDefault ? '★ Default' : '☆ Make default'}
                      </button>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => {
                          setRenaming(v.id);
                          setRenameText(v.name);
                        }}
                      >
                        Rename
                      </button>
                      <button type="button" className="link-btn danger" onClick={() => void dropView(v.id)}>
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* -------------------------------------------------------- view points */}
      <section className="section">
        <h3>Suggest a View Point</h3>
        {form === 'viewpoint' ? (
          <form onSubmit={submitSpot} className="stack">
            <LocationPicker
              value={draft}
              onChange={onDraftChange}
              picking={picking}
              onPickingChange={onPickingChange}
              myLocation={myLocation}
              onWantMyLocation={onWantMyLocation}
              onFlyTo={onFlyTo}
              requireRegion
            />
            <label className="field">
              <span>Name</span>
              <input value={spotName} onChange={(e) => setSpotName(e.target.value)} maxLength={80} placeholder="e.g. Lighthouse Park bluff" required />
            </label>
            <label className="field">
              <span>Area (optional)</span>
              <input value={spotRegion} onChange={(e) => setSpotRegion(e.target.value)} maxLength={60} placeholder="e.g. Whidbey" />
            </label>
            <label className="field">
              <span>Notes (optional)</span>
              <textarea
                value={spotNote}
                onChange={(e) => setSpotNote(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Parking, best tide, what you can see from here…"
              />
            </label>
            <label className="check">
              <input type="checkbox" checked={spotHike} onChange={(e) => setSpotHike(e.target.checked)} />
              <span>Reaching it takes a hike</span>
            </label>
            {spotError && <p className="form-error">{spotError}</p>}
            <div className="row gap">
              <button type="submit" className="btn btn-primary" disabled={spotBusy}>
                {user.role === 'admin' ? 'Add View Point' : 'Submit for Review'}
              </button>
              <button type="button" className="btn" onClick={closeForm}>
                Cancel
              </button>
            </div>
            {user.role !== 'admin' && (
              <small className="hint">
                Only you and the admins see it until it has been approved.
              </small>
            )}
          </form>
        ) : (
          <>
            <p className="muted">Know a good shore spot that isn't on the map? Add it.</p>
            <button type="button" className="btn" onClick={() => openForm('viewpoint')}>
              Add a View Point
            </button>
          </>
        )}

        {mine.length > 0 && (
          <>
            <h4>Your Suggestions</h4>
            <ul className="item-list">
              {mine.map((s) => (
                <li key={s.id} className="item item-col">
                  <div className="item-head">
                    <span className="item-title">{s.name}</span>
                    <span className={`badge badge-${s.status}`}>{s.status}</span>
                  </div>
                  {s.status === 'rejected' && s.reviewNote && <div className="item-meta">Reviewer note: {s.reviewNote}</div>}
                  <div className="item-actions">
                    <button type="button" className="link-btn" onClick={() => onFlyTo(s.lat, s.lon, 14)}>
                      Show
                    </button>
                    <a className="link-btn" href={googleMapsPinUrl(s.lat, s.lon)} target="_blank" rel="noopener noreferrer">
                      Google Maps
                    </a>
                    <button type="button" className="link-btn danger" onClick={() => void withdraw(s.id)}>
                      {s.status === 'pending' ? 'Withdraw' : 'Remove'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* -------------------------------------------------------------- account */}
      <section className="section">
        <details>
          <summary>Account Settings</summary>
          <form onSubmit={savePassword} className="stack">
            <h4>Change Password</h4>
            <label className="field">
              <span>Current password</span>
              <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} autoComplete="current-password" required maxLength={200} />
            </label>
            <label className="field">
              <span>New password</span>
              <input type="password" value={pwNext} onChange={(e) => setPwNext(e.target.value)} autoComplete="new-password" required minLength={8} maxLength={200} />
              <small>At least 8 characters. Other signed-in devices will be signed out.</small>
            </label>
            {pwMessage && <p className={pwMessage.ok ? 'form-ok' : 'form-error'}>{pwMessage.text}</p>}
            <button type="submit" className="btn">
              Change Password
            </button>
          </form>

          <form onSubmit={removeAccount} className="stack danger-zone">
            <h4>Delete Account</h4>
            <p className="muted">
              Removes your account, home location and saved views. View points you added that were already approved stay on the map.
            </p>
            <label className="field">
              <span>Confirm with your password</span>
              <input type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} autoComplete="current-password" required maxLength={200} />
            </label>
            {delError && <p className="form-error">{delError}</p>}
            <button type="submit" className="btn btn-danger">
              Delete My Account
            </button>
          </form>
        </details>
      </section>
    </div>
  );
}
